import React from "react";
import { HiCheck, HiOutlinePause, HiOutlineSparkles, HiOutlineX } from "react-icons/hi";
import Loader from "components/common/Loader/Loader";
import FloatingPopover, {
  FLOATING_POPOVER_MODE,
  FLOATING_POPOVER_PLACEMENT,
  FLOATING_POPOVER_VARIANT,
} from "components/common/FloatingPopover";
import { JOB_STATUS } from "../model/bulkAnalyseModel";
import { useBulkAnalyse } from "../BulkAnalyseContext";

const BulkAnalyseStatusBar = () => {
  const {
    enabled,
    job,
    counts,
    percent,
    etaLabel,
    resumeAllowed,
    resumeTotal,
    retryAllowed,
    retryTotal,
    stopLabel,
    retryLoading,
    dismissLoading,
    openJob,
    resume,
    retry,
    dismissFinished,
  } = useBulkAnalyse();

  if (!enabled || !job) return null;

  const running = job.status === JOB_STATUS.RUNNING;
  if (!running && job.banner_dismissed) return null;
  const stopped = job.status === JOB_STATUS.STOPPED;
  const failedWhileRunning = running && counts.failed > 0;

  const checked = counts.done + counts.failed;
  const showProgress = running && !failedWhileRunning;
  const totalLabel = counts.total === 1 ? "timesheet" : "timesheets";
  let title = `Bulk Analyse is analysing ${checked} of ${counts.total} ${totalLabel}.`;
  let subtitle = "You can leave this page. Each timesheet gets a score as it completes.";
  let button = "See progress";
  let onButton = openJob;
  let tone = "running";

  if (failedWhileRunning) {
    title = `${counts.failed} failed · ${counts.done} saved · ${counts.remaining} still queued`;
    subtitle = "Failed timesheets can be retried when this run finishes.";
    tone = "failed";
  } else if (stopped && resumeAllowed) {
    const waitingLabel = resumeTotal === 1 ? "timesheet" : "timesheets";
    title = `Bulk Analyse paused ${resumeTotal} ${waitingLabel}.`;
    subtitle = retryAllowed
      ? `${retryTotal} failed. Retry those when you are ready.`
      : "Resume when you are ready. Timesheets already analysed are kept.";
    button = retryLoading ? "Resuming…" : `Resume ${resumeTotal}`;
    onButton = resume;
    tone = "paused";
  } else if (stopped) {
    title = `${stopLabel} · ${counts.done} saved`;
    subtitle = retryAllowed
      ? "Retry the timesheets that failed."
      : "Timesheets already analysed are kept.";
    if (retryAllowed) {
      button = retryLoading ? "Retrying…" : `Retry ${retryTotal}`;
      onButton = retry;
    }
    tone = "failed";
  } else if (!running && retryAllowed) {
    title = `${counts.failed} failed · ${counts.done} saved`;
    subtitle = "This run is completed. Retry only the failed timesheets.";
    button = retryLoading ? "Retrying…" : `Retry ${retryTotal}`;
    onButton = retry;
    tone = "failed";
  } else if (!running) {
    title = `${counts.done} timesheets analysed`;
    subtitle = "Review the scores, then approve or reject each timesheet.";
    button = "View results";
    tone = "complete";
  }

  const shell =
    tone === "failed"
      ? "border-[#FDBA74] bg-[#FFF7ED]"
      : tone === "complete"
        ? "border-y border-[#A7F3D0] border-l-2 border-l-[#34D399] bg-[#F0FDF4]"
        : tone === "paused"
          ? "border-y border-[#FDE68A] border-l-2 border-l-[#FBBF24] bg-[#FFFBEB]"
          : "border-y border-[#BFDBFE] border-l-2 border-l-[#93C5FD] bg-[#F8FAFF]";
  const titleColor =
    tone === "failed" ? "text-[#9A3412]" : tone === "complete" ? "text-[#065F46]" : "text-[#1E3A8A]";
  const pip = tone === "failed" ? "bg-[#DC2626]" : tone === "complete" ? "bg-[#059669]" : "bg-[#4B6CB7]";

  return (
    <div
      role="status"
      aria-live="polite"
      className={`bulk-analyse-status shrink-0 border-y px-3 py-3 sm:px-5 ${shell}`}
    >
      <div className={`bulk-analyse-status__layout${showProgress ? " is-running" : ""}`}>
      <button
        type="button"
        onClick={openJob}
        className="bulk-analyse-status__copy flex items-center gap-3 text-left"
      >
        <span className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
          tone === "complete"
            ? "bg-[#D1FAE5] text-[#047857]"
            : tone === "paused"
              ? "bg-[#FEF3C7] text-[#B45309]"
              : tone === "running"
                ? "bg-[#DBEAFE] text-[#1D4ED8]"
                : "bg-white shadow-sm"
        }`}>
          {tone === "complete" ? (
            <HiCheck className="h-4 w-4" aria-hidden="true" />
          ) : tone === "paused" ? (
            <HiOutlinePause className="h-4 w-4" aria-hidden="true" />
          ) : tone === "running" ? (
            <HiOutlineSparkles className="h-4 w-4" aria-hidden="true" />
          ) : (
            <span className={`h-2.5 w-2.5 rounded-full ${pip}`} />
          )}
        </span>
        <span className="bulk-analyse-status__text">
          {tone === "complete" ? (
            <span className="bulk-analyse-status__heading block text-[15px] font-semibold leading-5 text-[#0F172A]">
              Bulk Analyse completed <span className="font-bold tabular-nums">{counts.done}</span>{" "}
              {counts.done === 1 ? "timesheet" : "timesheets"}.
            </span>
          ) : tone === "running" || tone === "paused" ? (
            <span className="bulk-analyse-status__heading block text-[15px] font-semibold leading-5 text-[#0F172A]">
              {title}
            </span>
          ) : (
            <span className={`bulk-analyse-status__heading block text-sm font-bold ${titleColor}`}>{title}</span>
          )}
          <span className={`bulk-analyse-status__sub mt-0.5 block text-[13px] font-normal leading-5 ${tone === "failed" ? "text-[#9A3412]" : "text-[#475569]"}`}>
            {subtitle}
          </span>
        </span>
      </button>
      {showProgress ? (
        <div className="bulk-analyse-status__progress">
          <span className="bulk-analyse-status__count text-xs font-semibold tabular-nums text-[#1E3A8A]">
            {counts.done + counts.failed} of {counts.total}
          </span>
          <span className="bulk-analyse-status__bar h-[13px] overflow-hidden rounded-full border border-[#CBD5E1] bg-white">
            <span
              className="bulk-analyse-bar-fill relative block h-full overflow-hidden rounded-full bg-[#059669]"
              style={{ width: `${percent}%` }}
            >
              <span className="bulk-analyse-bar-pulse pointer-events-none absolute inset-y-0 left-0" />
            </span>
          </span>
          <span className="bulk-analyse-status__percent text-sm font-bold tabular-nums text-[#047857]">{percent}%</span>
          <span className="bulk-analyse-status__eta text-left text-xs font-semibold tabular-nums text-[#334155]">
            {etaLabel}
          </span>
        </div>
      ) : null}
      <div className="bulk-analyse-status__actions">
      <button
        type="button"
        onClick={onButton}
        disabled={(onButton === retry || onButton === resume) && retryLoading}
        className="bulk-analyse-status__action background-gradient inline-flex h-9 shrink-0 items-center justify-center rounded-lg px-4 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        {button}
      </button>
      {running ? null : (
        <FloatingPopover
          mode={FLOATING_POPOVER_MODE.HOVER}
          variant={FLOATING_POPOVER_VARIANT.BRAND}
          placement={FLOATING_POPOVER_PLACEMENT.BOTTOM}
          maxWidth={240}
          zIndex={10080}
          content={
            <div className="px-3.5 py-3 text-left text-[13px] leading-snug text-[#0F172A]">
              <p className="font-semibold">Dismiss</p>
              <p className="mt-1 text-[#334155]">
                Hides this result. The scores stay on each timesheet.
              </p>
            </div>
          }
        >
          <button
            type="button"
            onClick={dismissFinished}
            disabled={dismissLoading}
            aria-label={dismissLoading ? "Closing bulk analysis" : "Dismiss"}
            aria-busy={dismissLoading}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#FEE2E2] text-[#B91C1C] ring-1 ring-[#FECACA] transition-colors hover:bg-[#FECACA] focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0F172A] disabled:cursor-wait disabled:hover:bg-[#FEE2E2]"
          >
            {dismissLoading ? (
              <Loader cssClass="!mr-0 h-4 w-4 !text-[#B91C1C]" />
            ) : (
              <HiOutlineX className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </FloatingPopover>
      )}
      </div>
      </div>
    </div>
  );
};

export default BulkAnalyseStatusBar;
